import { useState } from "react";
import { useDispatch } from "react-redux";
import { HiOutlineTrash } from "react-icons/hi";
import Badge from "../shared/Badge";
import Button from "../shared/Button";
import Modal from "../shared/Modal";
import {
  changeUserRoleAction,
  removeOrgUserAction,
} from "../../redux/actions/usersActions";

const ROLE_BADGE = {
  admin: "dark",
  viewer: "default",
};

const UsersList = ({ orgUsers, currentUserId, isAdmin, organisationId }) => {
  const dispatch = useDispatch();
  const [userToRemove, setUserToRemove] = useState(null);
  const [removeError, setRemoveError] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);
  const [roleError, setRoleError] = useState("");

  const adminCount = orgUsers.filter(
    (u) => Array.isArray(u.roles) && u.roles.includes("admin")
  ).length;

  const getUserRole = (user) => {
    if (Array.isArray(user.roles)) {
      return user.roles.includes("admin") ? "admin" : "viewer";
    }
    return "viewer";
  };

  const handleRoleChange = async (user, newRole) => {
    setRoleError("");
    try {
      await dispatch(
        changeUserRoleAction(organisationId, user._id, newRole)
      );
    } catch (err) {
      setRoleError(err.message || "Failed to change role");
    }
  };

  const handleRemoveConfirm = async () => {
    if (!userToRemove) return;
    setIsRemoving(true);
    setRemoveError("");
    try {
      await dispatch(removeOrgUserAction(organisationId, userToRemove._id));
      setUserToRemove(null);
    } catch (err) {
      setRemoveError(err.message || "Failed to remove user");
    } finally {
      setIsRemoving(false);
    }
  };

  const isLastAdmin = (user) =>
    getUserRole(user) === "admin" && adminCount === 1;

  return (
    <div>
      {roleError && (
        <div className="mb-4 p-3 rounded-md border border-signature-coral bg-signature-coral/5">
          <p className="text-body-md text-signature-coral">{roleError}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-hairline bg-canvas">
        <div className="overflow-x-auto">
          <table className="w-full text-body-md">
            <thead>
              <tr className="text-[12px] uppercase tracking-wide bg-surface-soft text-ink border-b border-hairline">
                <th className="px-6 py-4 text-left font-medium">User</th>
                <th className="px-6 py-4 text-left font-medium">Email</th>
                <th className="px-6 py-4 text-left font-medium">Role</th>
                {isAdmin && (
                  <th className="px-6 py-4 text-left font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {orgUsers.map((user) => {
                const role = getUserRole(user);
                const isSelf = user._id === currentUserId;
                const cannotRemove = isLastAdmin(user);

                return (
                  <tr key={user._id} className="hover:bg-surface-soft">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 flex items-center justify-center rounded-full bg-surface-soft border border-hairline text-ink text-body-md font-medium">
                          {(user.name || user.email || "?")[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-ink">
                          {user.name || "—"}
                          {isSelf && (
                            <span className="ml-2 text-[11px] text-muted">
                              (you)
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-body text-muted">
                      {user.email}
                    </td>
                    <td className="px-6 py-4">
                      {isAdmin && !isSelf ? (
                        <select
                          value={role}
                          onChange={(e) =>
                            handleRoleChange(user, e.target.value)
                          }
                          className="h-8 px-3 bg-canvas border border-hairline rounded-sm text-body-md text-ink focus:outline-none focus:border-info-border"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <Badge variant={ROLE_BADGE[role] ?? "default"}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </Badge>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4">
                        <button
                          onClick={() => {
                            setRemoveError("");
                            setUserToRemove(user);
                          }}
                          disabled={isSelf || cannotRemove}
                          title={
                            cannotRemove
                              ? "Cannot remove the last admin"
                              : isSelf
                              ? "Cannot remove yourself"
                              : "Remove user"
                          }
                          className="p-2 rounded-md text-muted hover:text-signature-coral hover:bg-surface-soft transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <HiOutlineTrash className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {orgUsers.length === 0 && (
            <div className="text-center py-12 text-muted text-body-md">
              No users found.
            </div>
          )}
        </div>
      </div>

      {/* Remove user confirmation modal */}
      <Modal
        isOpen={!!userToRemove}
        onClose={() => {
          setUserToRemove(null);
          setRemoveError("");
        }}
        title="Remove User"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setUserToRemove(null);
                setRemoveError("");
              }}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemoveConfirm}
              disabled={isRemoving}
            >
              {isRemoving ? "Removing..." : "Remove"}
            </Button>
          </>
        }
      >
        <p className="text-body-md text-body">
          Remove{" "}
          <span className="font-medium text-ink">
            {userToRemove?.name || userToRemove?.email}
          </span>{" "}
          from this organisation? They will lose access immediately.
        </p>
        {removeError && (
          <div className="mt-4 p-3 rounded-md border border-signature-coral bg-signature-coral/5">
            <p className="text-body-md text-signature-coral">{removeError}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UsersList;
